"use client";

import { useTranslations } from "next-intl";
import { ChangeEvent, useEffect, useMemo, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import cx from "classnames";

// Definition
import { GameParamsType } from "@/domains/games/types";

// Components
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { Switcher } from "@/components/UI/Switcher";
import BackButton from "@/components/UI/BackButton/BackButton";
import IconSprite from "@/components/UI/IconSprite";
import AuthModal from "@/components/Modal/Auth/AuthModal";

// Hooks
import { useGameFetch } from "@/domains/games/hooks/use-game-fetch";
import { useGameDemoInit } from "@/domains/games/hooks/use-game-demo-init";
import { useGameInit } from "@/domains/games/hooks/use-game-init";

// Utils
import st from "./index.module.scss";
import { PUBLIC_IMAGE_URL } from "@/network/http-config";
import { routes } from "@/definitions/book";
import { useStore } from "@/hooks/use-store";
import screenfull from "screenfull";
import { useMediaQuery } from "@mui/material";

type GamePropsType = { searchParams: GameParamsType };

export default function Game({ searchParams }: GamePropsType) {
  const t = useTranslations("GamePage");
  const router = useRouter();
  const pathname = usePathname();
  const { game, loadingGameFetch } = useGameFetch();
  const isDemo = Boolean(searchParams.demo && game?.provider.demo);
  const isTabletOrMobile = useMediaQuery("only screen and (max-width: 1024px)");
  const { user } = useStore();

  const gameDemoPayload = useMemo(
    () => ({
      gameId: game?.id,
      demo: isDemo,
    }),
    [game?.id, isDemo],
  );

  const { gameDemoUrl, onMutateGameDemoInit, errorGameDemoInit } = useGameDemoInit(gameDemoPayload);

  const gamePayload = useMemo(
    () => ({
      gameId: game?.id,
      demo: isDemo,
    }),
    [game?.id, isDemo],
  );

  const { gameUrl, onMutateGameInit, errorGameInit } = useGameInit(gamePayload);

  const isRealDisabled = !user.isAuth && !isDemo;
  const isRealActive = user && !isDemo;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const triggerFullscreen = () => {
    const iframe = iframeRef.current;

    if (!iframe) return;

    if (screenfull.isEnabled) {
      screenfull.request(iframe);
    }
  };

  useEffect(() => {
    if (!loadingGameFetch && !game?.provider.demo) {
      router.replace(pathname, { scroll: false });
    }

    if (!game?.provider.restricted) {
      if (game?.id && isDemo) {
        onMutateGameDemoInit();
      } else if (game?.id && !isDemo) {
        onMutateGameInit();
      }
    }
  }, [game?.id, isDemo, loadingGameFetch]);

  const handleSwitchMode = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    router.replace(pathname + (checked ? "?demo=true" : ""), { scroll: false });
  };

  if (isTabletOrMobile) {
    router.push("/");
  }

  return (
    <div className="section">
      <div className={st.game__wrapper}>
        <div className={st.game__header}>
          <div className={st.game__header_title}>
            <BackButton />
            <h2 className={st.game__header_title__name}>{game?.name}</h2>
          </div>
          <div className={st.game__header_right}>
            <div className={st.game__header_right_logo}>
              <Link href={`${routes.provider}/${game?.provider.slug}`}>
                {game?.provider && (
                  <Image
                    className={st.game__header_right_logo__img}
                    src={`${PUBLIC_IMAGE_URL}${game?.provider.image}`}
                    alt={game.provider.name}
                    width={145}
                    height={50}
                  />
                )}
              </Link>
            </div>

            <div className={st.game__header_right_nav}>
              {game?.provider.demo && (
                <Switcher
                  label={t("demo")}
                  onChange={handleSwitchMode}
                  checked={Boolean(searchParams.demo)}
                />
              )}
              <button
                className={st.game__header_right_nav__btn}
                disabled={isRealDisabled}
                onClick={triggerFullscreen}
              >
                <IconSprite
                  name="full-screen"
                  className={st.game__header_right_nav__fullscreen_icon}
                />
              </button>
            </div>
          </div>
        </div>
        {game?.provider.restricted ? (
          <div className={cx(st.game__body)}>
            <div className={cx(st.game__body_restricted)}>
              <Image src="/images/vpn.svg" height={256} width={256} alt="" />
              <p>Игра недоступна в вашем регионе.</p>
              <p>Пожалуйста, включите VPN. Для продолжения игры.</p>

              <p className={cx(st.game__body_restricted_title)}>Рекомендуемые страны:</p>

              <ul className={cx(st.game__body_restricted_list)}>
                <li>Chile</li>
                <li>Turkey</li>
                <li>Poland</li>
                <li>Moldova</li>
                <li>Indonesia</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className={cx(st.game__body, { [st["game__body--need-login"]]: isRealDisabled })}>
            {isRealDisabled && <AuthModal activeTab="registration" isLoggedIn={false} />}
            {isRealActive && gameUrl && (
              <iframe
                ref={iframeRef}
                allowFullScreen
                className={st.game__body_frame}
                src={gameUrl}
              ></iframe>
            )}

            {isDemo && gameDemoUrl && (
              <iframe
                ref={iframeRef}
                allowFullScreen
                className={st.game__body_frame}
                src={gameDemoUrl}
              ></iframe>
            )}
            {(errorGameDemoInit || errorGameInit) && (
              <div className={st.game__body_error}>Game Init Server Error</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
